import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, FileSpreadsheet, Image as ImageIcon, Trash2, Edit3, Check, X, AlertCircle, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ManifestEditor({ product, onChange }) {
  const [manifest, setManifest] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/products/${product.id}/manifest`);
      setManifest(data);
    } catch {
      setManifest(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [product.id]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/products/${product.id}/manifest`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      if (data.error_hint) {
        toast.warning(data.error_hint);
      } else {
        toast.success(`Parsed ${data.count} items · Total MSRP ${fmtMoney(data.total_msrp)}`);
      }
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manifest upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAll = async () => {
    if (!window.confirm("Delete this manifest entirely? The pallet product itself will stay.")) return;
    try {
      await api.delete(`/products/${product.id}/manifest`);
      toast.success("Manifest cleared");
      await load();
      onChange?.();
    } catch { toast.error("Delete failed"); }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/products/${product.id}/manifest/${id}`);
      await load();
    } catch { toast.error("Could not delete row"); }
  };

  const saveEdit = async (id, patch) => {
    try {
      await api.patch(`/products/${product.id}/manifest/${id}`, patch);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (loading) return <div className="text-sm text-gray-500 p-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading manifest…</div>;

  const hasManifest = manifest && manifest.items && manifest.items.length > 0;

  return (
    <div className="border border-gray-200" data-testid="admin-manifest-editor">
      <div className="bg-[#FAFAFA] px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="ipv-eyebrow text-[#0A1C40]">Pallet Manifest</div>
          {hasManifest && (
            <div className="text-xs text-gray-600 mt-0.5">
              {manifest.count_lines} items · Total MSRP {fmtMoney(manifest.total_msrp)} ·
              {manifest.savings_dollars > 0 ? (
                <span className="text-green-700 font-semibold ml-1">
                  saves customer {fmtMoney(manifest.savings_dollars)} ({manifest.savings_percent}% off)
                </span>
              ) : <span className="ml-1">no savings calculated</span>}
            </div>
          )}
        </div>
        {hasManifest && (
          <button onClick={removeAll} className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 inline-flex items-center gap-1" data-testid="manifest-delete-all-btn">
            <Trash2 className="w-3 h-3" /> Delete manifest
          </button>
        )}
      </div>

      {/* Upload zone */}
      <div className="p-4">
        <label
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-[#0A1C40] hover:bg-blue-50/40 cursor-pointer p-6 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
          data-testid="manifest-upload-dropzone"
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin text-[#0A1C40]" />
              <div className="font-bold text-sm">Parsing manifest…</div>
              <div className="text-xs text-gray-500">AI is reading every line — usually takes 5-15 seconds</div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <ImageIcon className="w-5 h-5 text-gray-400" />
                <FileText className="w-5 h-5 text-gray-400" />
                <FileSpreadsheet className="w-5 h-5 text-gray-400" />
              </div>
              <div className="font-bold text-sm">
                {hasManifest ? "Replace manifest" : "Upload manifest"}
              </div>
              <div className="text-xs text-gray-500 text-center max-w-md">
                Photo of the printed sheet (JPG/PNG) · PDF · Excel (.xlsx) · CSV.
                AI auto-extracts name, qty &amp; MSRP for every row.
              </div>
            </>
          )}
          <input
            type="file"
            className="hidden"
            accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.csv,.xlsx,.xls,image/*,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="manifest-file-input"
            disabled={uploading}
          />
        </label>
      </div>

      {/* Items table */}
      {hasManifest && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="manifest-admin-table">
            <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-widest">
              <tr>
                <th className="p-2">Qty</th>
                <th className="p-2">Item</th>
                <th className="p-2 text-right">MSRP each</th>
                <th className="p-2 text-right">MSRP total</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {manifest.items.map((it) => (
                <ManifestRow
                  key={it.id}
                  item={it}
                  editing={editingId === it.id}
                  onEdit={() => setEditingId(it.id)}
                  onCancel={() => setEditingId(null)}
                  onSave={(patch) => saveEdit(it.id, patch)}
                  onDelete={() => removeItem(it.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ManifestRow({ item, editing, onEdit, onCancel, onSave, onDelete }) {
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(item.quantity);
  const [msrp, setMsrp] = useState(item.msrp ?? "");

  useEffect(() => {
    setName(item.name);
    setQuantity(item.quantity);
    setMsrp(item.msrp ?? "");
  }, [item, editing]);

  if (editing) {
    return (
      <tr className="border-t border-gray-100 bg-blue-50/40" data-testid={`manifest-edit-row-${item.id}`}>
        <td className="p-2"><input type="number" value={quantity} min="1" onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)} className="w-16 border border-gray-300 px-1 py-0.5" data-testid="manifest-edit-qty" /></td>
        <td className="p-2"><input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 px-1 py-0.5" data-testid="manifest-edit-name" /></td>
        <td className="p-2 text-right"><input type="number" step="0.01" value={msrp} onChange={(e) => setMsrp(e.target.value)} placeholder="0.00" className="w-24 border border-gray-300 px-1 py-0.5 text-right" data-testid="manifest-edit-msrp" /></td>
        <td className="p-2 text-right text-xs text-gray-500">{msrp && quantity ? `$${(parseFloat(msrp) * quantity).toFixed(2)}` : "—"}</td>
        <td className="p-2 text-right whitespace-nowrap space-x-1">
          <button onClick={() => onSave({ name, quantity, msrp })} className="text-xs bg-green-600 text-white font-bold px-2 py-1 hover:bg-green-700 inline-flex items-center gap-1" data-testid="manifest-save-btn">
            <Check className="w-3 h-3" /> Save
          </button>
          <button onClick={onCancel} className="text-xs px-2 py-1 hover:bg-gray-100" data-testid="manifest-cancel-btn">
            <X className="w-3 h-3 inline" />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-t border-gray-100 ${item.needs_review ? "bg-amber-50/40" : ""}`} data-testid={`manifest-row-${item.id}`}>
      <td className="p-2 font-bold whitespace-nowrap">{item.quantity}×</td>
      <td className="p-2">
        <div className={item.needs_review ? "text-gray-400 italic" : ""}>
          {item.needs_review && <AlertCircle className="w-3 h-3 inline mr-1 text-amber-600" />}
          {item.name}
        </div>
        {item.sku && <div className="text-xs text-gray-400 font-mono">{item.sku}</div>}
      </td>
      <td className="p-2 text-right whitespace-nowrap">{item.msrp ? fmtMoney(item.msrp) : <span className="text-gray-300">—</span>}</td>
      <td className="p-2 text-right whitespace-nowrap">{item.msrp_total ? fmtMoney(item.msrp_total) : <span className="text-gray-300">—</span>}</td>
      <td className="p-2 text-right whitespace-nowrap space-x-1">
        <button onClick={onEdit} className="text-xs px-2 py-1 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1" data-testid={`manifest-edit-btn-${item.id}`}>
          <Edit3 className="w-3 h-3" /> Edit
        </button>
        <button onClick={onDelete} className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`manifest-delete-btn-${item.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </td>
    </tr>
  );
}
